<template>
	<transition name="show-sidebar">
		<div
      v-if="showSidebar"
      class="sidebar"
			style="min-height: calc(100vh - 60px)"
		>
      <div class="sidebar-header d-flex pl-3">
        <img
          class="user-avatar-img d-inline"
          src="/assets/icons/orange/user-account-secondary.svg"
          alt="user account navbar avatar"
        />
        <div class="d-inline pl-3">
          <h5 class="mb-0">Hi, {{ first_name }}</h5>
          <p v-if="only_paused_available_recurrable" class="mb-0">
            Your subscription is currently paused
          </p>
          <p v-else-if="has_next_date" class="mb-0">
            Next shipment date
            <span class="t-capitalize">{{ getMonthDate(nextShipmentDate) }}</span>
          </p>
        </div>
      </div>
      <div class="sidebar-body">
        <div
          class="row w-100 ml-0 p-relative"
          v-for="route in routes_to_show"
          :key="route.id"
        >
          <router-link
            :class="{'sidebar-link col-12 px-0 d-flex': true, 'sidebar-link-active': routePath.startsWith(route.link)}"
            :to="route.link"
            @click="closeSidebar"
            :style="{
              height: '50px'
            }"
          >
            <div class="my-auto">
              <h5 class="t-uppercase mb-0">
                {{ route.name }}
              </h5>
            </div>
          </router-link>
          <hr class="divider divider-mob" />
        </div>
      </div>
      <div class="sidebar-bottom-banner row mx-0 w-100">
        <div class="col-4 col-sm-7 px-0 pl-3 pt-3">
          <p class="sidebar-bottom-title">Browse Shop</p>
          <a href="/coffees"  class="sidebar-bottom-link">
            <p>
              Coffees
              <img
                src="/assets/icons/secondary/chevron-rounded-right.svg"
                class="sidebar-bottom-icon d-inline"
                width="16" height="16"
                alt="user account link arrow"
              />
            </p>
          </a>
          <a href="/coffee-equipment" class="sidebar-bottom-link">
            <p class="mb-0">
              Equipment
              <img
                src="/assets/icons/secondary/chevron-rounded-right.svg"
                class="sidebar-bottom-icon d-inline"
                width="16" height="16"
                alt="user account link arrow"
              />
            </p>
          </a>
        </div>
        <div class="col-8 col-sm-5 px-0 sidebar-image-fade-left h-100">
          <img
            src="https://res.cloudinary.com/pactcoffee/image/upload/f_auto,q_auto/v1766403345/website-d2c/assets/kopi/account/PactCoffee-2023-V60-House-250g_2.jpg"
            class="w-100 h-100"
            alt="user account link arrow"
          />
        </div>
      </div>
      <login-button-mobile></login-button-mobile>
    </div>
	</transition>
</template>
<script setup>
import { toRefs, ref, computed, watch } from 'vue'
import { useAuthUser } from '../../utils/stores/modules/authUser'
import { useDateHelpers } from '../utils/composables/dateHelpers'
import LoginButtonMobile from '../utils/loginButtonMobile.vue'

const userStore = useAuthUser()
// Props (defineProps)
const props = defineProps({
  showSidebar: {
    type: Boolean,
    required: true,
  },
  nextShipmentDate: {
    type: String,
    required: false,
    default: ''
  },
  routePath: {
    type: String,
    required: true,
  }
})
// Emits (defineEmits)
// Reactive variables (inc composables)
const { nextShipmentDate, showSidebar } = toRefs(props)
const { getMonthDate } = useDateHelpers()
const routes = ref([
  // {
  //   id: 1,
  //   link: '/account/my-account',
  //   name: 'your account',
  //   isActive: false,
  // },
  {
    id: 1,
    link: '/account/my-subscription',
    name: 'your subscription',
    isActive: false,
  },
  {
    id: 2,
    link: '/account/order-history',
    name: 'order history',
    isActive: false,
  },
  {
    id: 3,
    link: '/account/rate-coffees',
    name: 'rate coffees',
    isActive: false,
  },
  {
    id: 4,
    link: '/account/details',
    name: 'account details',
    isActive: false,
  },
  {
    id: 5,
    link: '/account/share-a-coffee',
    name: 'Refer a Friend',
    isActive: false,
  },
  {
    id: 6,
    link: '/account/contact-us',
    name: 'contact us',
    isActive: false,
  }
])
// Computed
const routes_to_show = computed(() => routes.value)
const first_name = computed(() => userStore.firstName)
const recurrable_state_count = computed(() => userStore.recurrable_state_count)
const only_paused_available_recurrable = computed(() => {
  return recurrable_state_count.value?.active === 0 && recurrable_state_count.value?.paused > 0
})
const has_next_date = computed(() => {
  return nextShipmentDate.value
    && nextShipmentDate.value !== ''
    && nextShipmentDate.value !== undefined
    && recurrable_state_count.value.active > 0
})
// Methods
function closeSidebar() {
  userStore.closeSidebar()
}
// Watchers
watch(showSidebar, () => {
  window.scrollTo(0, 0)
})
// Lifecycle Hooks
// Expose (defineExpose)
</script>
<script>
export default {
	name: 'AccountSidebar',
}
</script>

<style lang="scss" scoped>
  .sidebar-body .sidebar-link h5 {
    color: #212427 !important;
    font-family: 'Apercu' !important;
    font-weight: 700 !important;
  }
</style>
<style lang="scss">
.show-sidebar-enter-from {
	opacity: 0;
	transform: translateX(-100%);
}

.show-sidebar-leave-to {
	opacity: 0;
	transform: translateX(-100%);
}

.show-sidebar-enter-active,
.show-sidebar-leave-active {
	transition: all 0.4s ease-in-out;
}

.sidebar-bottom-banner {
  /* ensure both columns share the same height */
  align-items: stretch !important;
  height: 150px;
  position: fixed;
  padding: 0 !important;
  bottom: 50px;
}

.sidebar-image-fade-left {
  position: relative;
  overflow: hidden;
  height: 100%;
  img {
    display: block;
    object-fit: cover;
    object-position: right;
  }
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient( to right, #f4f4f4 0%,  rgba(255, 255, 255, 0) 35% );
  }
}
.sidebar-bottom {
  &-title {
    color: #1D4045;
    text-align: left;
    /* XS-B */
    font-family: 'Apercu';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
  }
  &-link p {
    color: #1D4045;
    text-align: left;
    /* 2XS-BUTTON */
    font-family:'Apercu';
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  &-icon {
    width: 16px;
    height: 16px;
    padding: 2px;
  }
}
</style>
